import React, { useState, useEffect } from 'react';
import { Loader2, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { io } from 'socket.io-client';
import Logo from './Logo';

const UpdateOverlay = () => {
    const [updateState, setUpdateState] = useState({
        isActive: false,
        type: '', // 'start', 'progress', 'downloaded', 'error'
        percent: 0,
        text: ''
    });

    useEffect(() => {
        // Only run if we are in Electron Native App
        if (window.electronAPI && window.electronAPI.onUpdateMessage) {
            window.electronAPI.onUpdateMessage((event, data) => {
                console.log("Update Message received", data);

                if (data.type === 'start') {
                    setUpdateState({
                        isActive: true,
                        type: 'start',
                        percent: 0,
                        text: data.text
                    });
                } else if (data.type === 'progress') {
                    setUpdateState(prev => ({
                        ...prev,
                        type: 'progress',
                        percent: data.percent,
                        text: `Lade Update herunter... ${data.percent}%`
                    }));
                } else if (data.type === 'downloaded') {
                    setUpdateState(prev => ({
                        ...prev,
                        type: 'downloaded',
                        percent: 100,
                        text: data.text
                    }));

                    // Wait 2 seconds before silently restarting the app with the new version
                    setTimeout(() => {
                        window.electronAPI.installUpdate();
                    }, 2000);
                } else if (data.type === 'error') {
                    setUpdateState({
                        isActive: true,
                        type: 'error',
                        percent: 0,
                        text: data.text || 'Update fehlgeschlagen.'
                    });
                    // Auto-dismiss so the user can keep using the app
                    setTimeout(() => {
                        setUpdateState(prev => ({ ...prev, isActive: false }));
                    }, 6000);
                }
            });
        }
    }, []);

    useEffect(() => {
        // Connect to Socket.io for live updates
        const isDev = window.location.port === '4040';
        const backendUrl = isDev ? 'http://localhost:3000' : window.location.origin;
        
        const socket = io(backendUrl);
        
        socket.on('frontend-update-applied', (data) => {
            console.log('[LiveUpdater] Frontend update applied on daemon:', data);
            
            setUpdateState({
                isActive: true,
                type: 'progress',
                percent: 30,
                text: 'Live-Update empfangen...'
            });
            
            setTimeout(() => {
                setUpdateState(prev => ({
                    ...prev,
                    percent: 75,
                    text: 'Wende Hot-Fix an...'
                }));
                
                setTimeout(() => {
                    setUpdateState({
                        isActive: true,
                        type: 'downloaded',
                        percent: 100,
                        text: 'Live-Update erfolgreich angewendet!'
                    });
                    
                    setTimeout(() => {
                        if (window.electronAPI && window.electronAPI.relaunchApp) {
                            window.electronAPI.relaunchApp();
                        } else {
                            window.location.reload();
                        }
                    }, 1000);
                }, 600);
            }, 400);
        });
        
        return () => {
            socket.disconnect();
        };
    }, []);

    if (!updateState.isActive) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center auto-update-overlay bg-dark-primary/90 backdrop-blur-md animate-fadeIn">
            {/* Pulsing Backglow */}
            <div className="absolute w-[600px] h-[600px] rounded-full bg-accent-primary/5 blur-[100px] animate-pulse"></div>

            <div className="relative glass-panel rounded-3xl p-10 max-w-md w-full border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col items-center text-center">
                
                {/* Logo & Icon */}
                <div className="relative mb-6">
                    {updateState.type === 'downloaded' ? (
                        <div className="w-20 h-20 bg-emerald-500/10 rounded-2xl flex items-center justify-center border border-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                        </div>
                    ) : updateState.type === 'error' ? (
                        <div className="w-20 h-20 bg-red-500/10 rounded-2xl flex items-center justify-center border border-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                            <AlertTriangle className="w-10 h-10 text-red-400" />
                        </div>
                    ) : (
                        <div className="w-20 h-20 bg-accent-primary/10 rounded-2xl flex items-center justify-center border border-accent-primary/20 shadow-[0_0_20px_rgba(245,158,11,0.2)]">
                            <Download className="w-10 h-10 text-accent-primary animate-bounce opacity-80" />
                        </div>
                    )}
                </div>

                <h2 className="text-2xl font-bold font-display text-white mb-2">Gemule Update</h2>
                <p className="text-white/60 mb-8 min-h-[48px]">
                    {updateState.text}
                </p>

                {/* Progress Bar Container */}
                <div className="w-full bg-black/40 rounded-full h-3 mb-2 border border-white/5 overflow-hidden relative">
                    <div 
                        className="h-full bg-gradient-to-r from-accent-primary to-accent-secondary transition-all duration-300 ease-out"
                        style={{ width: `${updateState.percent}%` }}
                    />
                    
                    {/* Shimmer effect on progress bar */}
                    {updateState.type !== 'downloaded' && (
                        <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
                    )}
                </div>

                {/* Stats */}
                <div className="w-full flex justify-between items-center text-xs text-white/40 font-mono mt-1">
                    <span>{updateState.type === 'downloaded' ? 'Fertig' : 'Laden...'}</span>
                    <span className="text-accent-primary">{updateState.percent}%</span>
                </div>

                {/* Loader Spinner */}
                {updateState.type !== 'downloaded' ? (
                    <div className="mt-8 flex items-center gap-2 text-white/30 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Im Hintergrund</span>
                    </div>
                ) : (
                    <div className="mt-8 text-emerald-400/80 text-sm font-medium animate-pulse">
                        App startet in wenigen Sekunden neu...
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpdateOverlay;
